import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const LiningPage = () => {
  useEffect(() => {
    document.title = '삼원테크 | 라이닝 물탱크 - 에폭시 코팅 부식방지 스테인리스 물탱크';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 라이닝 물탱크. 에폭시·폴리우레탄·FRP 특수 코팅으로 부식 방지 및 수질 보호. 정수장·여과지·배수지 적합. 9단계 제작 공정, 위생안전 인증.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "라이닝 물탱크",
      "description": "특수 코팅 처리로 부식 방지 및 수질 보호 기능이 강화된 물탱크. 에폭시·폴리우레탄·FRP 라이닝.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/lining`,
      "brand": { "@type": "Brand", "name": "삼원테크" },
      "manufacturer": {
        "@type": "Organization",
        "name": "삼원테크",
        "url": import.meta.env.VITE_SITE_URL || 'https://example.com'
      },
      "material": "STS304 + 에폭시 코팅",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "KRW",
        "seller": { "@type": "Organization", "name": "삼원테크" }
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "제작과정", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process` },
          { "@type": "ListItem", "position": 3, "name": "라이닝 물탱크", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/process/lining` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-lining';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-lining')?.remove(); };
  }, []);

  const advantages = [
    {
      icon: 'ri-water-flash-line',
      title: '정수장·여과지·배수지 적합',
      description:
        '정수장, 여과지, 배수지 등 다양한 수처리 시설에 최적화된 라이닝 솔루션입니다.'
    },
    {
      icon: 'ri-shield-star-line',
      title: '뛰어난 내식성',
      description:
        '특수 코팅으로 부식을 완벽하게 차단하여 장기간 안정적인 사용이 가능합니다.'
    },
    {
      icon: 'ri-drop-line',
      title: '위생성 및 수밀성',
      description:
        '식수 기준에 적합한 위생적인 코팅으로 수밀성이 우수하여 누수를 방지합니다.'
    },
    {
      icon: 'ri-tools-line',
      title: '시공성 및 설치 편의성',
      description:
        '현장 시공이 용이하고 설치 편의성이 높아 공기 단축 및 비용 절감이 가능합니다.'
    },
    {
      icon: 'ri-building-2-line',
      title: '강성 우수 — 하중지지력·내구성',
      description:
        '강성이 우수한 패널로 하중지지력과 내구성이 뛰어나 다양한 환경에서 안정적입니다.'
    },
    {
      icon: 'ri-links-line',
      title: '콘크리트 결합력 향상으로 이탈방지',
      description:
        '콘크리트와의 결합력이 향상되어 이탈을 방지하고 구조적 안정성을 높입니다.'
    },
    {
      icon: 'ri-layout-grid-line',
      title: '평탄도 개선',
      description:
        '콘크리트 벽체나 바닥면에 부착 시 평탄도를 개선하여 균일한 마감을 제공합니다.'
    }
  ];

  const installationSteps = [
    {
      step: '01',
      title: '원자재 입고',
      description:
        '원자재 입고 후 인수검사를 실시하여 품질 기준에 적합한 자재만 사용합니다.',
      icon: 'ri-inbox-archive-line',
      image: 'https://public.readdy.ai/ai/img_res/4ef8d407a356a7073bd73d9a08422c07.jpg'
    },
    {
      step: '02',
      title: '절단',
      description:
        '스테인리스 강대 및 강판을 설계 도면에 따라 정밀하게 절단합니다.',
      icon: 'ri-scissors-cut-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/25864530adb3c40d76167b0804cfd3a5.png'
    },
    {
      step: '03',
      title: '자재 가공',
      description:
        '기둥 및 굴곡 부위를 정밀하게 가공하여 설치 준비를 완료합니다.',
      icon: 'ri-hammer-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/c0f2f912e9d6a8078cd806ac9aa19072.png'
    },
    {
      step: '04',
      title: '평철 설치 작업',
      description:
        '물탱크의 기초가 되는 평철을 정확하게 설치합니다.',
      icon: 'ri-layout-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/fff661fa3f3af33019d2cdde75b6195a.png'
    },
    {
      step: '05',
      title: '판넬 설치 작업',
      description:
        '라이닝 패널을 순차적으로 설치하고 체결합니다.',
      icon: 'ri-layout-grid-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/3410af797c7709d4d5a7e4d291866c59.png'
    },
    {
      step: '06',
      title: '배관 설치 작업',
      description:
        '급수 및 배수를 위한 배관 시스템을 설치합니다.',
      icon: 'ri-git-branch-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/b5a7272d5fd1b39779df72cbfd802469.png'
    },
    {
      step: '07',
      title: '기둥 라이닝 작업',
      description:
        '건축 구조물과 연결되는 기둥 부분의 라이닝 작업을 진행합니다.',
      icon: 'ri-building-2-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/9182f9d6dbdff2569cab2771091bef0d.png'
    },
    {
      step: '08',
      title: '에폭시 코팅(선택)',
      description:
        '특수 에폭시를 여러 층으로 코팅하여 내식성과 수밀성을 확보합니다.',
      icon: 'ri-paint-brush-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/71eba33eaebdac5c2e62cd8466eabad7.png'
    },
    {
      step: '09',
      title: '완성',
      description:
        '최종 검사 및 시운전을 거쳐 라이닝 물탱크 제작을 완료합니다.',
      icon: 'ri-checkbox-circle-line',
      image: 'https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/fb810072cccc840bcce707f1219b94a5.png'
    }
  ];

  const liningTypes = [
    {
      title: '에폭시 라이닝',
      description: '식수용으로 가장 많이 사용되는 라이닝 타입',
      features: ['식수 적합', '내화학성 우수', '밀착력 강함', '위생적'],
      icon: 'ri-drop-line',
      color: 'from-teal-500 to-teal-600'
    },
    {
      title: '폴리우레탄 라이닝',
      description: '내마모성과 유연성이 뛰어난 라이닝 타입',
      features: ['내마모성', '유연성', '내충격성', '장기 내구성'],
      icon: 'ri-shield-line',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      title: 'FRP 라이닝',
      description: '강도와 내식성이 우수한 고급 라이닝 타입',
      features: ['고강도', '내식성 최고', '경량화', '장수명'],
      icon: 'ri-star-line',
      color: 'from-amber-500 to-amber-600'
    }
  ];

  const specifications = [
    { label: '본체 재질', value: 'STS304,STS316 등' },
    { label: '코팅 방식', value: '에폭시' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Link
                  to="/products"
                  className="text-teal-200 hover:text-white transition-colors cursor-pointer"
                >
                  제품소개
                </Link>
                <i className="ri-arrow-right-s-line text-teal-200"></i>
                <span>라이닝 물탱크</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
                라이닝 물탱크
              </h1>
              <p className="text-xl md:text-2xl text-teal-50 max-w-3xl leading-relaxed">
                에폭시 코팅 처리로 부식 방지 및 수질 보호 기능이 강화된 제품입니다
              </p>
            </motion.div>
          </div>
        </section>

        {/* Product Image */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="relative w-full h-[600px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=industrial%20water%20tank%20with%20protective%20epoxy%20lining%20coating%20in%20clean%20modern%20facility%20with%20white%20background%20showing%20interior%20coating%20details%20and%20quality%20finish&width=1600&height=600&seq=lining1&orientation=landscape"
                alt="라이닝 물탱크"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                라이닝 물탱크의 장점
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                라이닝 물탱크만의 차별화된 7가지 핵심 장점을 확인하세요
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {advantages.slice(0, 4).map((adv, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center text-white text-2xl mb-5">
                    <i className={adv.icon}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                    {adv.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {adv.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {advantages.slice(4).map((adv, index) => (
                <motion.div
                  key={index + 4}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (index + 4) * 0.1 }}
                  className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center text-white text-2xl mb-5">
                    <i className={adv.icon}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                    {adv.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {adv.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Lining Types */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {liningTypes.map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100"
                >
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Installation Process */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                라이닝 물탱크 제작과정
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                체계적인 9단계 제작 프로세스로 최고 품질을 보장합니다
              </p>
            </div>

            <div className="space-y-8">
              {installationSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-200 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    <div className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="flex items-center gap-6 mb-6">
                        <div className="text-6xl font-bold text-teal-600/20">
                          {step.step}
                        </div>
                        <div className="w-20 h-20 bg-teal-600 rounded-2xl flex items-center justify-center text-white text-3xl flex-shrink-0">
                          <i className={step.icon}></i>
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    <div className={`relative h-[400px] ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-20 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                제품 사양
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                라이닝 물탱크의 상세 사양을 확인하세요
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white rounded-3xl shadow-xl overflow-hidden border border-teal-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-teal-200">
                {specifications.map((spec, index) => (
                  <div
                    key={index}
                    className="p-8 text-center hover:bg-white transition-colors duration-300"
                  >
                    <div className="text-sm font-semibold text-teal-600 mb-2 uppercase tracking-wide">
                      {spec.label}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {spec.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                라이닝 물탱크 제작 상담
              </h2>
              <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto leading-relaxed">
                전문 상담원이 고객님의 요구사항에 맞는 최적의 솔루션을
                제안해드립니다
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-block bg-white text-teal-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  상담 문의하기
                </Link>
                <Link
                  to="/products"
                  className="inline-block bg-teal-500 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-400 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  다른 제품 보기
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LiningPage;
